import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

// Feature: Auth
import Login from './features/auth/Login';
// Feature: Home / Dashboard
import Dashboard from './features/home/Dashboard';
// Feature: Bookings
import Bookings from './features/bookings/Bookings';
import KioskCheckIn from './features/bookings/KioskCheckIn';
import Kiosk from './features/bookings/Kiosk';
import CustomerBooking from './features/bookings/CustomerBooking';
// Feature: Job Cards
import JobCards from './features/job-cards/JobCards';
import JobCardPrint from './features/job-cards/JobCardPrint';
// Feature: Customers (+ vehicles, leads, accounts)
import Customers from './features/customers/Customers';
import CustomerDetails from './features/customers/CustomerDetails';
import LeadDetails from './features/customers/LeadDetails';
import Vehicles from './features/customers/Vehicles';
import VehicleDetails from './features/customers/VehicleDetails';
import Accounts from './features/customers/Accounts';
import AccountDetails from './features/customers/AccountDetails';
import MemberAccountDetails from './features/customers/MemberAccountDetails';
import MemberSignup from './features/public/MemberSignup';
import MemberPortal from './features/public/MemberPortal';
// Feature: Invoices / Quotes
import Invoices from './features/invoices/Invoices';
import InvoiceEdit from './features/invoices/InvoiceEdit';
import QuoteDetail from './features/invoices/QuoteDetail';
import QuoteReview from './features/invoices/QuoteReview';
import InvoiceTemplates from './features/invoices/InvoiceTemplates';
// Feature: Comms / Marketing
import Comms from './features/comms/Comms';
import MarketingCentre from './features/comms/MarketingCentre';
// Feature: Parts
import PartsCatalog from './features/parts/PartsCatalog';
// Feature: Loan Cars
import LoanCars from './features/loan-cars/LoanCars';
import LoanCarForm from './features/loan-cars/LoanCarForm';
import LoanCarDetails from './features/loan-cars/LoanCarDetails';
import LoanCarQRPrint from './features/loan-cars/LoanCarQRPrint';
import LoanCarKiosk from './features/loan-cars/LoanCarKiosk';
// Feature: Financials
import Financials from './features/financials/Financials';
// Feature: Super Admin (settings, reports, audit, sms, templates, data-import)
import Settings from './features/super-admin/Settings';
import Reports from './features/super-admin/Reports';
import AuditLogs from './features/super-admin/AuditLogs';
import SuperAdminDashboard from './features/super-admin/SuperAdminDashboard';
import SuperAdminTemplates from './features/super-admin/SuperAdminTemplates';
import SuperAdminServiceMappings from './features/super-admin/SuperAdminServiceMappings';
import SuperAdminSms from './features/super-admin/SuperAdminSms';
import DataImport from './features/super-admin/DataImport';
// Feature: Mechanic
import MechanicDashboard from './features/mechanic/MechanicDashboard';
import MyLeave from './features/mechanic/MyLeave';
import PerformanceReview from './features/mechanic/PerformanceReview';
// Feature: Documentation (Knowledge Base & Confluence)
import Documentation from './features/documentation/Documentation';

// Shared components (not feature-scoped)
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureGate from './components/FeatureGate';
import { Toaster } from 'sonner';
import { SiteProvider } from './context/SiteContext';
import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Install the Authorization-header fallback EARLY (before any route
// loads). Necessary because Safari/iOS sometimes drops our session
// cookie via ITP; this lets us authenticate via a bearer token stored
// in localStorage as a belt-and-suspenders.
import { installAuthFetchInterceptor, adoptUrlSessionToken } from './lib/authFetch';
installAuthFetchInterceptor();
// Adopt a session token handed over via URL hash (#st=…) by an opener tab
// BEFORE any route/auth check fires — see lib/authFetch.js withSessionHash.
adoptUrlSessionToken();

function AppRouter() {
  const location = useLocation();

  // Synchronously check for session_id in URL fragment (NOT query params)
  // This prevents race conditions by processing session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/book" element={<CustomerBooking />} />
        <Route path="/kiosk" element={<Kiosk />} />
        <Route path="/q/:token" element={<QuoteReview />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
        <Route path="/kiosk/check-in" element={<ProtectedRoute><KioskCheckIn /></ProtectedRoute>} />
        <Route path="/job-cards" element={<ProtectedRoute><JobCards /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
        <Route path="/customers/:customerId" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
        <Route path="/leads/:leadId" element={<ProtectedRoute><FeatureGate feature="comms"><LeadDetails /></FeatureGate></ProtectedRoute>} />
        <Route path="/comms" element={<ProtectedRoute><FeatureGate feature="comms"><Comms /></FeatureGate></ProtectedRoute>} />
        <Route path="/vehicles" element={<ProtectedRoute><Vehicles /></ProtectedRoute>} />
        <Route path="/vehicles/:vehicleId" element={<ProtectedRoute><VehicleDetails /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/invoices/new" element={<ProtectedRoute><InvoiceEdit /></ProtectedRoute>} />
        <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoiceEdit /></ProtectedRoute>} />
        <Route path="/quotes/:quoteId" element={<ProtectedRoute><QuoteDetail /></ProtectedRoute>} />
        <Route path="/invoice-templates" element={<ProtectedRoute><InvoiceTemplates /></ProtectedRoute>} />
        <Route path="/parts-catalog" element={<ProtectedRoute><PartsCatalog /></ProtectedRoute>} />
        <Route path="/loan-cars" element={<ProtectedRoute><FeatureGate feature="loan_cars"><LoanCars /></FeatureGate></ProtectedRoute>} />
        <Route path="/loan-cars/qr-print" element={<ProtectedRoute><FeatureGate feature="loan_cars"><LoanCarQRPrint /></FeatureGate></ProtectedRoute>} />
        <Route path="/loan-car-form/:loanId" element={<LoanCarForm />} />
        <Route path="/loan-car-details/:token" element={<LoanCarDetails />} />
        <Route path="/loan-car-kiosk/:siteId" element={<LoanCarKiosk />} />
        <Route path="/job-cards/print/:jobCardId" element={<ProtectedRoute><JobCardPrint /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/financials" element={<ProtectedRoute><FeatureGate feature="financials"><Financials /></FeatureGate></ProtectedRoute>} />
        <Route path="/marketing-centre" element={<ProtectedRoute><FeatureGate feature="marketing_centre"><MarketingCentre /></FeatureGate></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
        <Route path="/super-admin" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="/super-admin/templates" element={<ProtectedRoute><SuperAdminTemplates /></ProtectedRoute>} />
        <Route path="/super-admin/service-mappings" element={<ProtectedRoute><SuperAdminServiceMappings /></ProtectedRoute>} />
        <Route path="/super-admin/sms" element={<ProtectedRoute><SuperAdminSms /></ProtectedRoute>} />
        <Route path="/documentation" element={<ProtectedRoute><FeatureGate feature="documentation"><Documentation /></FeatureGate></ProtectedRoute>} />
        <Route path="/data-import" element={<ProtectedRoute><DataImport /></ProtectedRoute>} />
        <Route path="/accounts" element={<ProtectedRoute><FeatureGate feature="accounts"><Accounts /></FeatureGate></ProtectedRoute>} />
        <Route path="/accounts/:accountId" element={<ProtectedRoute><FeatureGate feature="accounts"><AccountDetails /></FeatureGate></ProtectedRoute>} />
        <Route path="/memberships/:memberId" element={<ProtectedRoute><FeatureGate feature="memberships"><MemberAccountDetails /></FeatureGate></ProtectedRoute>} />
        <Route path="/member-signup" element={<MemberSignup />} />
        <Route path="/member-portal" element={<MemberPortal />} />
        <Route path="/my-workshop" element={<ProtectedRoute><MechanicDashboard /></ProtectedRoute>} />
        <Route path="/leave" element={<ProtectedRoute><FeatureGate feature="leave"><MyLeave /></FeatureGate></ProtectedRoute>} />
        <Route path="/performance-review" element={<ProtectedRoute><FeatureGate feature="performance"><PerformanceReview /></FeatureGate></ProtectedRoute>} />
      </Routes>
      {/* ZoomControl is docked inside the sidebar (Layout.jsx) per user request,
          so it no longer overlaps the bottom chat widget. */}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        // User not authenticated yet
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    // Another tab logged out or switched to a different account — this
    // tab's UI/caches belong to the old identity and every API call now
    // authenticates as the new one (stale workshop data, print 404s).
    // Reload so the whole app rebuilds under the current session.
    const onSessionChange = (e) => {
      if (e.key === 'session_token' && e.oldValue !== e.newValue) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onSessionChange);
    return () => window.removeEventListener('storage', onSessionChange);
  }, []);

  return (
    <SiteProvider user={user}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </SiteProvider>
  );
}

export default App;
